import React, { useState, useEffect } from 'react';
import { branding } from '../constants/branding';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';

const getTransactionIcon = (category: string) => {
  switch (category) {
    case 'Coffee': return '☕';
    case 'Subscription': return '🎬';
    case 'Transfer': return '💸';
    case 'Transport': return '🚗';
    case 'Food': return '🍔';
    case 'Shopping': return '🛒';
    case 'Income': return '💰';
    case 'Health': return '🏥';
    default: return '💳';
  }
};

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [analytics, setAnalytics] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const token = useAppSelector((state) => state.auth.token);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchTransactions = async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    
    if (!token) {
      setError('Authentication token not available');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching transactions:', { page, pageSize });
      const response = await axiosInstance.get(`/transactions?page=${page}&size=${pageSize}`);
      
      console.log('Transactions response:', response);
      
      if (response.status === 200) {
        const data = response.data;
        console.log('Transactions data:', data);
        
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          const transactions = data.data?.transactions || data.data?.content || data.data || [];
          const totalPages = data.data?.totalPages || data.totalPages || 1;
          const currentPage = data.data?.currentPage || data.currentPage || page;
          
          console.log('Setting transactions:', { transactions, totalPages, currentPage });
          
          setTransactions(transactions);
          setTotalPages(totalPages);
          setCurrentPage(currentPage);
        } else {
          console.error('Failed to fetch transactions:', data.message);
          setError(data.message || 'Failed to fetch transactions');
        }
      } else {
        setError(`Server error: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      setError(error.message || 'An error occurred while fetching transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!token) {
        console.log('No token available for analytics');
        return;
      }

      try {
        console.log('Fetching analytics...');
        const response = await axiosInstance.get('/transactions/analytics');
        
        console.log('Analytics response:', response);
        
        if (response.status === 200) {
          const data = response.data;
          console.log('Analytics data:', data);
          
          // Check for both possible response formats: data.success or data.status === 'success'
          if (data.success || data.status === 'success') {
            setAnalytics(data.data);
          } else {
            console.error('Failed to fetch analytics:', data.message);
          }
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      }
    };
    fetchAnalytics();
  }, [token]);

  useEffect(() => {
    fetchTransactions(page, pageSize);
  }, [page, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const downloadTransactions = async () => {
    try {
      const response = await axiosInstance.get('/transactions/download', {
        responseType: 'blob'
      });
      
      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'transactions.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading transactions:', error);
    }
  };

  return (
    <div style={{
      fontFamily: branding.fontFamily,
      background: branding.pageBackground,
      padding: isMobile ? '24px 0' : 0,
      margin: 0,
      flex: 1
    }}>
      <div style={{
        maxWidth: 900,
        margin: isMobile ? '0 auto' : '32px auto 0 auto',
        padding: '0 16px',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: 32,
          gap: isMobile ? 24 : 0,
        }}>
          <div>
            <h1 style={{
              color: branding.textColor,
              fontWeight: 700,
              fontSize: isMobile ? 24 : 28,
              margin: 0,
              marginBottom: 8,
            }}>Transactions</h1>
            <div style={{ fontSize: isMobile ? 15 : 16, color: '#666' }}>
              Monitor your financial activity and spending patterns
            </div>
          </div>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div style={{
            background: '#8e44ad',
            borderRadius: 20,
            padding: isMobile ? 24 : 32,
            color: '#fff',
            marginBottom: 24,
            boxShadow: '0 4px 24px rgba(142,68,173,0.15)',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'flex-start',
              marginBottom: isMobile ? 16 : 24,
              gap: isMobile ? 16 : 0,
            }}>
              <div>
                <div style={{ fontSize: isMobile ? 14 : 16, opacity: 0.9, marginBottom: 8 }}>Monthly Income</div>
                <div style={{ fontSize: isMobile ? 32 : 36, fontWeight: 700 }}>{branding.currency.symbol}{analytics.totalIncome}</div>
              </div>
              <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                <div style={{ fontSize: isMobile ? 14 : 16, opacity: 0.9, marginBottom: 8 }}>Monthly Expenses</div>
                <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#fff' }}>
                  {branding.currency.symbol}{analytics.totalExpenses}
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.2)',
            }}>
              <div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>Monthly Balance</div>
                <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600 }}>
                  ${analytics.balance}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, opacity: 0.9 }}>Status</div>
                <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600 }}>
                  {analytics.balance >= 0 ? 'Positive' : 'Negative'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Spending Categories */}
        {analytics && analytics.topCategories && (
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 24,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            marginBottom: 32,
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: 24,
              color: branding.textColor,
              fontSize: 20,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontSize: 20 }}>📈</span>
              Top Spending Categories
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 20
            }}>
              {analytics.topCategories.map((cat: any) => (
                <div key={cat.name} style={{
                  background: '#f8f9fa',
                  borderRadius: 16,
                  padding: 20,
                  border: '1px solid #eee',
                }}>
                  <div style={{
                    color: branding.textColor,
                    fontWeight: 600,
                    fontSize: 16
                  }}>{cat.name}</div>
                  <div style={{
                    color: branding.textColor,
                    fontWeight: 700,
                    fontSize: 24,
                    marginBottom: 12
                  }}>${cat.amount}</div>
                  <div style={{
                    width: '100%',
                    height: 6,
                    background: '#e0e0e0',
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${cat.percentage}%`,
                      height: '100%',
                      background: '#8e44ad',
                      borderRadius: 3,
                      transition: 'width 0.3s'
                    }} />
                  </div>
                  <div style={{
                    color: '#666',
                    fontSize: 13,
                    marginTop: 2
                  }}>{cat.percentage.toFixed(1)}% of total</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transactions Table */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: isMobile ? 24 : 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          marginBottom: 32,
        }}>
          <h3 style={{
            margin: 0,
            marginBottom: 24,
            color: branding.textColor,
            fontSize: isMobile ? 18 : 20,
            fontWeight: 600
          }}>
            Transaction History
          </h3>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>Loading...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: branding.errorColor, padding: 32 }}>{error}</div>
          ) : (
            <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
              <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', fontSize: isMobile ? 14 : 16 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Description</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Category</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(transactions || []).map((tx: any) => (
                    <React.Fragment key={tx.id}>
                      <tr style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => setExpanded(expanded === tx.id ? null : tx.id)}>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{
                              background: '#f2f2f7',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 16
                            }}>{getTransactionIcon(tx.category)}</span>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{tx.description}</div>
                              <div style={{ color: '#666', fontSize: 12 }}>{tx.details || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 6,
                            fontSize: isMobile ? 13 : 14,
                            fontWeight: 600,
                            background: '#f8f9fa',
                            color: branding.textColor,
                          }}>
                            {tx.category}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', whiteSpace: 'nowrap' }}>
                          {tx.transactionDate ? new Date(tx.transactionDate).toLocaleDateString() : ''}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: typeof tx.amount === 'number' && tx.amount < 0 ? branding.errorColor : branding.successColor, whiteSpace: 'nowrap' }}>
                          {typeof tx.amount === 'number' ? (tx.amount < 0 ? '-' : '+') + branding.currency.symbol + Math.abs(tx.amount).toFixed(2) : ''}
                        </td>
                      </tr>
                      {expanded === tx.id && (
                        <tr style={{ background: '#f8f9fa' }}>
                          <td colSpan={4} style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                              gap: 16,
                              fontSize: 14
                            }}>
                              <div>
                                <strong style={{ color: branding.textColor, fontWeight: 600 }}>Status:</strong>
                                <span style={{
                                  marginLeft: 8,
                                  padding: '4px 8px',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: '#e8f5e8',
                                  color: '#10b981',
                                }}>{tx.status}</span>
                              </div>
                              <div>
                                <strong style={{ color: branding.textColor, fontWeight: 600 }}>Details:</strong>
                                <span style={{ marginLeft: 8, color: '#666' }}>{tx.details || ''}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            borderTop: '1px solid #eee',
            paddingTop: 20
          }}>
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
              style={{
                fontSize: 22,
                width: 36,
                height: 36,
                minWidth: 0,
                padding: 0,
                borderRadius: '50%',
                border: page === 0 ? '1.5px solid #eee' : '1.5px solid #8e44ad',
                background: 'transparent',
                color: page === 0 ? '#bbb' : '#8e44ad',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'border 0.2s, color 0.2s',
              }}
              aria-label="Previous page"
            >
              {'<'}
            </button>
            <span style={{ fontWeight: 700, color: '#8e44ad', fontSize: 17, minWidth: 60, textAlign: 'center', letterSpacing: 1 }}>
              {page + 1} <span style={{ fontWeight: 400, fontSize: 15, color: '#888' }}>/ {totalPages}</span>
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages - 1}
              style={{
                fontSize: 22,
                width: 36,
                height: 36,
                minWidth: 0,
                padding: 0,
                borderRadius: '50%',
                border: page === totalPages - 1 ? '1.5px solid #eee' : '1.5px solid #8e44ad',
                background: 'transparent',
                color: page === totalPages - 1 ? '#bbb' : '#8e44ad',
                cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'border 0.2s, color 0.2s',
              }}
              aria-label="Next page"
            >
              {'>'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions; 